import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import SubcontractorCorporateInfoForm from "components/form/subcontractor/SubcontractorCorporateInfoForm";
import SubcontractorPersonalInfoForm from "components/form/subcontractor/SubcontractorPersonalInfoForm";
import ValidationModal from "components/modal/ValidationModal";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { QUERY_STATUS, POPUP_STATUS } from "constants/constants";
import { SUBCONTRACTOR_FORM_DEFAULT_VALUES } from "constants/forms";
import { formatCreatedData } from "utils/subcontractorHelper";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useSubcontractorContext } from "api/directory/subcontractor/SubcontractorProvider";
import { useUserContext } from "api/user/UserProvider";
import { useProfileUploadSetup } from "utils/customHooks";
import colors from "assets/styles/mixins/colors.scss";

const CreateSubcontractor = () => {
  const {
    control,
    formState: { errors },
    register,
    unregister,
    setValue,
    watch,
    clearErrors,
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues: SUBCONTRACTOR_FORM_DEFAULT_VALUES,
  });
  const { fetchBranches, branches } = useBranchesContext();
  const { lang, userScope, setApiErrorResponse } = useUserContext();
  const {
    subcontractorCreate,
    createdSubcontractorStatus,
    setCreatedSubcontractorStatus,
    createdSubcontractor,
    apiErrorResponse,
  } = useSubcontractorContext();
  const { getPersonsByProfile, personsByProfileList } = usePersonContext();
  const { currencyList, currencyListGetter } = useCurrencyContext();
  const {
    createdFile,
    setCreatedFile,
    createFile,
    uploadProfilePicture,
    setFileById,
  } = useFileContext();
  const { picture, tempUserId } = useProfileUploadSetup();
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchBranches();
    currencyListGetter();
  }, [fetchBranches, currencyListGetter]);

  const getOptions = useCallback(
    (search, profile = "") => {
      getPersonsByProfile({ search, profile });
    },
    [getPersonsByProfile]
  );

  const onSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await subcontractorCreate(info);
    } catch {
      // Provider already set createdSubcontractorStatus to ERROR and apiErrorResponse
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onError = () => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  const resetForm = () => {
    reset(SUBCONTRACTOR_FORM_DEFAULT_VALUES);
    window.scrollTo(0, 0);
  };

  const formatData = (data) => {
    const formattedData = formatCreatedData(data);
    onSubmit(formattedData);
    return setIsOpen(true);
  };

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
    }
  }, [apiErrorResponse]);

  const value = useMemo(
    () => ({
      branches,
      currencyList,
      getOptions,
      lang,
      personsByProfileList,
      cellHighlight,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      setCode,
      setStatus,
      apiErrorResponse,
      tempUserId,
    }),
    [
      branches,
      lang,
      personsByProfileList,
      currencyList,
      cellHighlight,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      apiErrorResponse,
      tempUserId,
    ]
  );

  return (
    <>
      <ValidationModal
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (createdSubcontractorStatus === QUERY_STATUS.ERROR) return;
          else if (
            createdSubcontractorStatus === QUERY_STATUS.SUCCESS &&
            createdSubcontractor?.slug
          ) {
            navigate(
              `/${userScope}/subcontractor/${createdSubcontractor?.slug}`
            );
          }
        }}
        status={createdSubcontractorStatus}
      />
      <InfoContext.Provider value={value}>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />
        <form
          onSubmit={(e) => {
              clearErrors();
              handleSubmit(formatData, onError)(e);
            }}
          style={{ paddingTop: "2rem" }}
        >
          <SubcontractorPersonalInfoForm
            watch={watch}
            setError={setError}
            clearErrors={clearErrors}
            control={control}
            errors={errors}
            register={register}
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
          />
          <SubcontractorCorporateInfoForm
            watch={watch}
            unregister={unregister}
            control={control}
            errors={errors}
            register={register}
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
          />
          <div className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              onClick={resetForm}
              type="button"
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              color="btn-medium gray-700"
              backgroundColor={
                createdSubcontractorStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
              disabled={createdSubcontractorStatus === QUERY_STATUS.LOADING}
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
            >
              <FormattedMessage
                id="button_form_validation"
                defaultMessage="Valider la création"
              />
            </Button>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};

export default CreateSubcontractor;
